import { createFileRoute, Link } from "@tanstack/react-router";
import { LoanCategoryPage } from "@/components/laneguide/LoanCategoryPage";
import { PAGE_FAQ } from "@/components/laneguide/PageFaq";

const SITE = "https://svensklaneguide.se";
const URL = `${SITE}/sms-lan-1000-kr-swish`;
const TITLE = "Akut SMS-Lån Från 1000 kr Via Swish | Svensk Låneguide";
const DESC =
  "Akut SMS-lån från 1000 kr med utbetalning via Swish och realtidsbetalning. Jämför långivare med små belopp, utan UC och snabb utbetalning – lugnt och ärligt.";

export const Route = createFileRoute("/sms-lan-1000-kr-swish")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "Akut SMS-Lån Från 1000 kr Via Swish" },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Akut SMS-Lån Från 1000 kr Via Swish" },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: SITE },
            { "@type": "ListItem", position: 2, name: "SMS-lån", item: `${SITE}/smslan` },
            { "@type": "ListItem", position: 3, name: "Akut SMS-lån från 1000 kr via Swish", item: URL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: (PAGE_FAQ["smslan"] ?? []).map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: () => (
    <LoanCategoryPage
      page="smslan-1000"
      productLabel="Akut SMS-lån från 1 000 kr"
      headingBefore="Akut SMS-Lån"
      underlined="Från 1000 kr"
      headingAfter="Via Swish"
      subtitle={
        <>
          När det är akut räknas varje minut. Här hittar du långivare som beviljar{" "}
          <Link to="/nyhet/$slug" params={{ slug: "akut-sms-lan-1000-kr-swish" }} className="text-primary underline underline-offset-2">
            akuta SMS-lån från 1 000 kr
          </Link>{" "}
          med utbetalning via Swish eller realtidsbetalning – ofta inom minuter, dygnet runt och utan UC. De
          aktörer som lånar ut allra minst ligger högst upp, därefter följer långivare från 2 000–3 000 kr.
        </>
      }
      featuredTitleTemplate={(m) => `Toppval för akuta SMS-lån i ${m}`}
      showCostDisclosure
      belowList={<BelowContent />}
    />
  ),
});

function BelowContent() {
  return (
    <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
      <div className="rounded-lg border border-border bg-card p-6 sm:p-10">
        <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
          Akut SMS-lån via Swish – så fungerar det
        </h2>
        <p className="mt-4 text-[15px] leading-relaxed text-foreground/80">
          Behöver du pengar i dag och känner att tiden rinner iväg? Du är inte ensam, och vi tänker inte döma.
          Här får du både det snabba svaret på hur pengarna kommer fram – och den ärliga bilden av vad ett
          litet lån faktiskt kostar.
        </p>

        <div className="mt-10 space-y-8">
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">Därför nämns Swish så ofta</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              Swish och andra realtidsbetalningar går utanför bankernas ordinarie clearing. Det är hela
              förklaringen till att pengarna kan nå kontot på minuter, även på kvällar, helger och röda dagar.
              Använder långivaren vanlig clearing godkänns ansökan direkt men pengarna bokförs nästa bankdag.
            </p>
          </div>
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">
              Ansök dygnet runt – men läs villkoren
            </h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              BankID och kreditupplysningssystemen är öppna dygnet runt, så ansökan kan skickas när som helst.
              Utbetalningen kan däremot vara begränsad hos aktörer som gör manuell granskning. Kontrollera
              alltid utbetalningsvillkoren innan du signerar om du behöver pengarna i dag.
            </p>
          </div>
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">Vad ett akut lån kostar</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              Vi listar endast lån med en återbetalningstid på minst 61 dagar (3 månader). Lånar du
              3 000 kr på 6 månader med 23 procents nominell ränta och 29 kr i aviavgift blir
              månadskostnaden cirka 563 kr och den totala återbetalningen omkring 3 378 kr – plus
              eventuell uppläggningsavgift, som ofta är den enskilt största posten. Jämför därför total
              återbetalning i kronor och effektiv ränta, inte bara månadskostnaden.
            </p>
          </div>
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">Utan UC vid akut behov</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              Många mikrolånegivare gör kreditprövningen via Creditsafe eller Bisnode i stället för UC.
              Kreditprövning sker alltid enligt lag – lån helt utan kreditupplysning finns inte hos seriösa
              aktörer. Se{" "}
              <Link to="/lan-utan-uc" className="text-primary underline underline-offset-2">
                lån utan UC
              </Link>{" "}
              för hela listan.
            </p>
          </div>
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">Om betalningen blir sen</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              Kontakta långivaren direkt om du ser att det blir tight – de allra flesta kan lägga upp en
              avbetalningsplan. Väntar du i stället tillkommer påminnelseavgift, dröjsmålsränta och i
              förlängningen risk för en betalningsanmärkning som ligger kvar i tre år. Läs mer hos{" "}
              <a
                href="https://hallakonsument.se"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
              >
                Hallå konsument
              </a>
              .
            </p>
          </div>
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">Ett lån i taget</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              Flera mikrolån samtidigt är den vanligaste vägen till betalningsproblem. Har du redan krediter är
              ett{" "}
              <Link to="/samla-lan" className="text-primary underline underline-offset-2">
                samlingslån
              </Link>{" "}
              nästan alltid billigare än ytterligare ett smålån. Behöver du bara mer tid kan ett{" "}
              <Link to="/snabblan-1000-kr" className="text-primary underline underline-offset-2">
                snabblån från 1 000 kr
              </Link>{" "}
              med längre löptid vara ett lugnare alternativ.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
